import { FileText, X } from "lucide-react";
import { useEffect } from "react";

import { useGetJobById } from "@elation/api-client/src/stitch-web";

import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Progress } from "@src/shared/components/ui/progress";
import { cn } from "@src/shared/lib/utils";

import type { BulkDocumentJob } from "./useBulkDocumentJobs";

interface BulkJobMetadata {
  step?:
    | "extracting_identifiers"
    | "matching"
    | "matched"
    | "unmatched"
    | "ambiguous"
    | "extracting"
    | "persisting"
    | "extracted";
  reason?:
    | "insufficient_identifiers"
    | "no_dob_lastname_match"
    | "multiple_candidates"
    | "firstname_mismatch"
    | "firstname_unverified"
    | "exact_match";
  matchedPatientId?: number | null;
  matchedPatientName?: string | null;
  opportunitiesCreated?: number;
  bulkUploadId?: string;
}

type Outcome =
  | "in_progress"
  | "extracted"
  | "unmatched"
  | "ambiguous"
  | "failed";

function deriveOutcome(
  status: string | undefined,
  isFailed: boolean,
  metadata: BulkJobMetadata | undefined,
): Outcome {
  if (isFailed) return "failed";
  if (status !== "completed") return "in_progress";
  const step = metadata?.step;
  if (step === "extracted") return "extracted";
  if (step === "unmatched") return "unmatched";
  if (step === "ambiguous") return "ambiguous";
  return "extracted"; // sensible fallback if step missing
}

function deriveProgress(
  status: string | undefined,
  outcome: Outcome,
  metadata: BulkJobMetadata | undefined,
): { value: number; subtitle: string } {
  if (outcome === "failed")
    return { value: 100, subtitle: "Processing failed" };
  if (outcome === "unmatched") {
    return {
      value: 100,
      subtitle: "We couldn't match this to a patient. Please check the file.",
    };
  }
  if (outcome === "ambiguous") {
    const reason = metadata?.reason;
    if (reason === "firstname_mismatch") {
      return {
        value: 100,
        subtitle: "First-name didn't match the candidate",
      };
    }
    if (reason === "firstname_unverified") {
      return { value: 100, subtitle: "Couldn't verify first-name match" };
    }
    return { value: 100, subtitle: "Multiple matching candidates" };
  }
  if (outcome === "extracted") {
    // The extracted (success) subtitle is rendered as rich copy in the card
    // body (with the opportunity count and matched patient name in medium
    // weight), so this string is only a non-visible fallback.
    return { value: 100, subtitle: "Complete" };
  }
  // in-progress, derive from step
  const step = metadata?.step;
  if (step === "extracting_identifiers") {
    return { value: 15, subtitle: "Identifying patient..." };
  }
  if (step === "matching") {
    return { value: 40, subtitle: "Looking up patient..." };
  }
  if (step === "matched") {
    return { value: 55, subtitle: "Matched — extracting care gaps..." };
  }
  if (step === "extracting") {
    return { value: 70, subtitle: "Extracting care gaps..." };
  }
  if (step === "persisting") {
    return { value: 85, subtitle: "Saving findings..." };
  }
  if (status === "running") {
    return { value: 30, subtitle: "Processing..." };
  }
  return { value: 15, subtitle: "Uploading..." };
}

const AUTO_DISMISS_DELAY_MS = 3000;

interface BulkJobCardProps {
  job: BulkDocumentJob;
  onRemove: (jobId: string) => void;
}

export const BulkJobCard = ({ job, onRemove }: BulkJobCardProps) => {
  const { data, isError } = useGetJobById(job.jobId, {
    query: {
      enabled: !job.failed,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return 2000;
      },
      refetchOnMount: (query) => {
        const status = query.state.data?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return true;
      },
    },
  });

  const status = data?.data?.status;
  const isFailed = job.failed || isError || status === "failed";
  const metadata = data?.data?.metadata as BulkJobMetadata | undefined;
  const outcome = deriveOutcome(status, isFailed, metadata);
  const { value, subtitle } = deriveProgress(status, outcome, metadata);

  const opportunityCount = metadata?.opportunitiesCreated ?? 0;
  const matchedPatientName = metadata?.matchedPatientName;

  useEffect(() => {
    // Only auto-dismiss green/red terminals. Amber (unmatched/ambiguous)
    // stays visible until the clinician explicitly removes it — they need
    // to know which docs didn't auto-process so they can chase them.
    if (outcome !== "extracted" && outcome !== "failed") return;
    const timer = setTimeout(() => onRemove(job.jobId), AUTO_DISMISS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [outcome, job.jobId, onRemove]);

  return (
    <HorizontalFlexbox
      alignItems="flex-start"
      gap="sm"
      className={cn(
        "rounded-lg border border-border bg-card p-3",
        outcome === "extracted" && "border-success/40 bg-success/5",
        outcome === "failed" && "border-destructive/40 bg-destructive/5",
        (outcome === "unmatched" || outcome === "ambiguous") &&
          "border-warning/40 bg-warning/5",
      )}
    >
      <FileText className="mt-0.5 size-5 shrink-0 text-accent" />
      <VerticalFlexbox className="min-w-0 flex-1">
        <HorizontalFlexbox
          alignItems="flex-start"
          justifyContent="space-between"
          gap="xs"
        >
          <Text sizing="sm" fontWeight={500} className="line-clamp-1">
            {job.filename}
          </Text>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => onRemove(job.jobId)}
            className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </HorizontalFlexbox>
        <Progress
          value={value}
          className={cn(
            "mt-2",
            outcome === "failed" && "[&>div]:bg-destructive",
            outcome === "extracted" && "[&>div]:bg-success",
            (outcome === "unmatched" || outcome === "ambiguous") &&
              "[&>div]:bg-warning",
          )}
        />
        <Text
          sizing="xs"
          appearance="subdued"
          className="mt-1"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {outcome === "extracted" ? (
            <>
              Found <span className="font-medium">{opportunityCount}</span> care
              opportunities
              {matchedPatientName ? (
                <>
                  {" "}
                  for <span className="font-medium">{matchedPatientName}</span>
                </>
              ) : null}
              .
            </>
          ) : (
            subtitle
          )}
        </Text>
      </VerticalFlexbox>
    </HorizontalFlexbox>
  );
};
